// Image upload service for Cloudinary integration

export interface UploadResponse {
  success: boolean;
  data?: {
    url: string;
    publicId: string;
    width: number;
    height: number;
    format: string;
    size: number;
  };
  error?: string;
}

// Upload endpoints require auth (ROO-109) — attach the bearer token.
function authHeader(): Record<string, string> {
  const token = localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const uploadService = {
  /**
   * Upload a single image to Cloudinary
   */
  async uploadImage(file: File): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: authHeader(),
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      return result;
    } catch (error) {
      console.error('Upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  },

  /**
   * Upload multiple images to Cloudinary
   */
  async uploadImages(files: File[]): Promise<UploadResponse> {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('images', file);
      });

      const response = await fetch('/api/upload/images', {
        method: 'POST',
        headers: authHeader(),
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      return result;
    } catch (error) {
      console.error('Multiple upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      };
    }
  },

  /**
   * Delete an image from Cloudinary
   */
  async deleteImage(publicId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`/api/upload/image/${encodeURIComponent(publicId)}`, {
        method: 'DELETE',
        headers: authHeader(),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      return result;
    } catch (error) {
      console.error('Delete error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Delete failed'
      };
    }
  },

  /**
   * Transform Cloudinary URL for different sizes/formats
   */
  transformImageUrl(url: string, transformations: {
    width?: number;
    height?: number;
    crop?: 'fill' | 'fit' | 'scale' | 'crop' | 'thumb' | 'limit';
    quality?: 'auto' | number;
    format?: 'auto' | 'jpg' | 'png' | 'webp';
  } = {}): string {
    if (!url.includes('cloudinary.com')) {
      return url; // Not a Cloudinary URL
    }

    const transformParts: string[] = [];

    if (transformations.width) transformParts.push(`w_${transformations.width}`);
    if (transformations.height) transformParts.push(`h_${transformations.height}`);
    if (transformations.crop) transformParts.push(`c_${transformations.crop}`);
    if (transformations.quality) transformParts.push(`q_${transformations.quality}`);
    if (transformations.format) transformParts.push(`f_${transformations.format}`);

    if (transformParts.length === 0) {
      return url;
    }

    const transformString = transformParts.join(',');
    
    // Insert transformation parameters into Cloudinary URL
    // Format: https://res.cloudinary.com/cloud_name/image/upload/transformations/public_id.format
    const urlParts = url.split('/upload/');
    if (urlParts.length === 2) {
      return `${urlParts[0]}/upload/${transformString}/${urlParts[1]}`;
    }

    return url;
  },

  /**
   * Generate thumbnail URL
   */
  getThumbnailUrl(url: string, size = 150): string {
    return this.transformImageUrl(url, {
      width: size,
      height: size,
      crop: 'fill',
      quality: 'auto',
      format: 'auto'
    });
  },

  /**
   * Generate responsive image URL
   */
  getResponsiveUrl(url: string, width: number): string {
    return this.transformImageUrl(url, {
      width,
      crop: 'scale',
      quality: 'auto',
      format: 'auto'
    });
  }
};